import { Box, Flex, Radio, RadioGroup, Stack, Text } from "@chakra-ui/react";
import { type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { SelectableCard } from "./SelectableCard";

interface RadioOptionGroupProps {
  options: ReadonlyArray<{ value: string; descriptionKey: string }>;
  value: string;
  onChange: (value: string) => void;
  // When true, clicking the already-selected option clears it (fires onChange("")).
  // Native radio semantics never deselect — opt in only where the field is genuinely
  // clearable and the cards-as-toggles read invites that affordance.
  allowDeselect?: boolean;
}

export const RadioOptionGroup = ({ options, value, onChange, allowDeselect = false }: RadioOptionGroupProps) => {
  const { t } = useTranslation();

  return (
    <RadioGroup value={value} onChange={onChange}>
      <Stack direction="column" spacing="3">
        {options.map((option) => (
          <SelectableCard
            key={option.value}
            as="label"
            isSelected={value === option.value}
            borderWidth="1px"
            px="4"
            py="3"
            // Label-click on the already-selected option would normally re-toggle the inner
            // <Radio> (no-op via RadioGroup since the value isn't changing). Intercept here
            // and emit a clear instead. preventDefault stops the label's default forwarding
            // to the input so the radio doesn't re-fire.
            onClick={
              allowDeselect && value === option.value
                ? (e: MouseEvent) => {
                    e.preventDefault();
                    onChange("");
                  }
                : undefined
            }
          >
            <Flex align="center" gap="3">
              <Radio value={option.value} colorScheme="teal" />
              <Box>
                <Text fontWeight="medium" fontSize="sm">
                  {t(option.value)}
                </Text>
                <Text fontSize="xs" color="smMuted">
                  {t(option.descriptionKey)}
                </Text>
              </Box>
            </Flex>
          </SelectableCard>
        ))}
      </Stack>
    </RadioGroup>
  );
};
